import * as React from "react";
import { format, parse } from "date-fns";
import { DatePicker, Form, NumberInput, Select, TextInput } from "components";
import { Context } from "components/medications/medication-group/components/provider/duck";
import { FormInstance, ROUTE_OPTIONS, useCurrentCase } from "duck";
import { getValidationSchema } from "./duck";
import styles from "./Form.module.css";

interface CardFieldFormProps {
  fieldName: string;
  value: number | string | null;
  medicationId: number | string;
  onFinish(): void;
}

const CardFieldForm: React.FC<CardFieldFormProps> = ({
  fieldName,
  value,
  medicationId,
  onFinish
}) => {
  const formRef = React.useRef<FormInstance<any>>(null);
  const { update } = React.useContext(Context);
  const { defibs, regionalBlocks, suspensions } = useCurrentCase(
    medicalCase => ({
      defibs: medicalCase?.data.client.patient.defibMedications || [],
      regionalBlocks: medicalCase?.data.client.patient.regionalBlocks || [],
      suspensions: medicalCase?.data.client.patient.suspensions || []
    })
  );
  const medication =
    defibs.find(
      med => med._id === medicationId || med.fakeId === medicationId
    ) ||
    regionalBlocks.find(
      med => med._id === medicationId || med.fakeId === medicationId
    );

  const isSuspension = !!suspensions.find(
    med => med._id === medicationId || med.fakeId === medicationId
  );

  React.useEffect(() => {
    formRef?.current?.setFocus(fieldName);
  }, [fieldName]);

  const defaultValue =
    fieldName === "timeAdministered"
      ? value
        ? parse(value as string, "h:mm aaaa", new Date())
        : new Date()
      : value;

  const onBlur = () => {
    // Added to validate form fields correctly. useField hook passes errors only for touched inputs, and inputs without useHookForm's onBlur are never touched
    // UseHookForm set focus on the first invalid input by default, impossible to blur input via preventDefault and document?.activeElement?.blur()
    formRef?.current?.setValue(
      fieldName,
      formRef?.current?.getValues()[fieldName],
      { shouldTouch: true }
    );
    formRef?.current?.submit();
  };

  return (
    <Form<any>
      validationSchema={getValidationSchema(
        fieldName,
        medication?.doseMin,
        medication?.doseMax,
        isSuspension
      )}
      defaultValues={{ [fieldName]: defaultValue }}
      onSubmit={values => {
        const updateResult = update({
          idOrFakeId: medicationId,
          [fieldName]:
            values[fieldName] instanceof Date
              ? format(values[fieldName], "HH:mm:ssxxx")
              : values[fieldName]
        });
        if (updateResult) {
          return updateResult.then(() => onFinish());
        }
        onFinish();
      }}
      innerRef={formRef}
    >
      {({ setValue }) => {
        const textInput = (
          <TextInput
            name={fieldName}
            type="text-area"
            label="Medication data"
            labelHidden
            minH={0}
            overflow="hidden"
            resize="none"
            onBlur={onBlur}
            size="sm"
            p="5px 4px 4px"
            fontWeight="normal"
            fontSize="md"
            lineHeight="21px"
            onFocus={e => {
              // scrollHeight + (borderTop + borderBottom)
              e.target.style.minHeight = `${e.target.scrollHeight + 2}px`;
              const valueLength = e.target.value.length;
              e.target.setSelectionRange(valueLength, valueLength);
            }}
            onChange={e => {
              setValue(fieldName, e.target.value);
              e.target.style.minHeight = `${e.target.scrollHeight + 2}px`;
            }}
          />
        );

        return (
          <>
            {{
              route: (
                <Select
                  name={fieldName}
                  isClearable={false}
                  label="Medication data"
                  labelHidden
                  options={ROUTE_OPTIONS}
                  onBlur={onBlur}
                  size="sm"
                  chakraStyles={{
                    valueContainer: provided => ({
                      ...provided,
                      paddingLeft: 0,
                      fontWeight: "normal",
                      fontSize: "md",
                      lineHeight: "21px"
                    }),
                    control: provided => ({
                      ...provided,
                      paddingLeft: "4px"
                    })
                  }}
                />
              ),
              timeAdministered: (
                <DatePicker
                  name={fieldName}
                  showTimeSelect
                  showTimeSelectOnly
                  dateFormat="h:mm aaaa"
                  label="Medication data"
                  labelHidden
                  size="sm"
                  className={styles.datePicker}
                  onBlur={onBlur}
                />
              ),
              labelInstructions: textInput,
              initials: textInput
            }[fieldName] || (
              <NumberInput
                clampValueOnBlur={false}
                name={fieldName}
                label="Medication data"
                labelHidden
                size="sm"
                onBlur={onBlur}
                inputFieldProps={{
                  pl: "4px",
                  fontWeight: "normal",
                  fontSize: "md",
                  lineHeight: "21px"
                }}
              />
            )}
          </>
        );
      }}
    </Form>
  );
};

export default CardFieldForm;
